import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGeneralPricing } from '../hooks/useCamper';

export function FaqSection() {
  const { generalPricing, isLoading: pricingLoading } = useGeneralPricing();
  
  // Function to format dates for display
  const formatDates = (datesString) => {
    if (!datesString) return '';
    // If dates contain "del" or "al", it's already formatted, return as is
    if (datesString.includes('del') || datesString.includes('al')) {
      return datesString;
    }
    // Otherwise, format from "11/04-20/04, 15/06-15/09" to "del 11/04 al 20/04 y del 15/06 a 15/09"
    if (datesString.includes(',')) {
      const parts = datesString.split(',').map(p => p.trim());
      return parts.map((part, index) => {
        if (part.includes('-')) {
          const [start, end] = part.split('-').map(d => d.trim());
          const connector = index === 0 ? 'del' : 'y del';
          const endConnector = index === 0 ? 'al' : 'a';
          return `${connector} ${start} ${endConnector} ${end}`;
        }
        return part;
      }).join(' ');
    }
    // Single date range
    if (datesString.includes('-')) {
      const [start, end] = datesString.split('-').map(d => d.trim());
      return `del ${start} al ${end}`;
    }
    // If it's "resto del año" or similar, return as is
    return datesString;
  };

  // Function to render pricing answer dynamically from Contentful
  const renderPricingAnswer = () => {
    if (pricingLoading || !generalPricing) {
      return <p>Cargando precios...</p>;
    }

    // Extract pricing data - format is [fechas, precio, mínimoDías]
    const highSeason = generalPricing.highSeason || [];
    const midSeason = generalPricing.midSeason || [];
    const lowSeason = generalPricing.lowSeason || [];

    // Format dates for better readability
    const highSeasonDates = formatDates(highSeason[0]);
    const midSeasonDates = formatDates(midSeason[0]);
    const lowSeasonDates = formatDates(lowSeason[0]);

    return (
      <>
        Los precios dependen de la época en la que quieras hacer tu escapada.
        <ul className="list-disc pl-5 mt-2 space-y-2">
          <li>
            Temporada alta {highSeasonDates && `(${highSeasonDates})`}: {highSeason[1] || 'N/A'} €/día, mínimo {highSeason[2] || 'N/A'} días
          </li>
          <li>
            Temporada media {midSeasonDates && `(${midSeasonDates})`}: {midSeason[1] || 'N/A'}€/día, mínimo {midSeason[2] || 'N/A'} días
          </li>
          <li>
            Temporada baja {lowSeasonDates && `(${lowSeasonDates})`}: {lowSeason[1] || 'N/A'} €/día, mínimo {lowSeason[2] || 'N/A'} días
          </li>
        </ul>
        <p className="mt-2">
          Estos precios incluyen 100 km/día con opción de aumentar kilometraje. Revisa todas las{" "}
          <Link to="/condiciones-alquiler" className="text-brand hover:text-brand/80 font-medium">
            condiciones de alquiler
          </Link>.
        </p>
      </>
    );
  };

  const faqs = [
    {
      question: "Cuánto cuesta?",
      isPricing: true // Flag to identify pricing question
    },
    {
      question: "Con qué edad se puede reservar?",
      answer: "A partir de 25 años. Recuerda que el conductor debe tener 3 años de carné de conducir."
    },
    {
      question: "Se aceptan mascotas?",
      answer: "Por supuesto! Solo recuerda mantener la limpieza y el cuidado del vehículo."
    },
    {
      question: "Es necesaria una fianza?",
      answer: (
        <>
          Sí, te recomendamos consultar todas las condiciones de tu alquiler{" "}
          <Link to="/condiciones-alquiler" className="text-brand hover:text-brand/80 font-medium">
            aquí
          </Link>.
        </>
      )
    },
    {
      question: "Cuántas personas pueden dormir?",
      answer: (
        <>
          Podrás desplazarte con hasta 5 personas, pero hay espacio para que duerman de 3 a 5 personas. Esto dependerá de cada camper, descúbrelas{" "}
          <Link to="/campers" className="text-brand hover:text-brand/80 font-medium">
            aquí
          </Link>.
        </>
      )
    },
    {
      question: "Tienen baño y ducha?",
      answer: "Todas nuestras campers cuentan con ducha de agua caliente. Algunas de nuestras campers cuentan con baño químico, pero no te preocupes que siempre podrás pedir un baño plegable."
    },
    {
      question: "Se puede viajar con niños?",
      answer: "Sí. Además contamos con ISOFIX en todas nuestras campers."
    },
    {
      question: "Están equipadas con portabicis?",
      answer: "Sí, todas nuestras campers cuentan con portabicis."
    },
    {
      question: "Hay sitio para meter mi tabla de surf?",
      answer: (
        <>
          Nuestra{" "}
          <Link to="/campers/lagos" className="text-brand hover:text-brand/80 font-medium">
            camper Lagos
          </Link>
          , tiene la opción de llevarlas en un soporte sobre el techo.
        </>
      )
    }
  ];

  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="w-full px-4 py-12 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          No te quedes con dudas!
        </h2>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-200">
              <button
                className="w-full flex justify-between items-center py-4 text-left"
                onClick={() => toggleFaq(index)}
              >
                <span className="text-lg font-medium">{faq.question}</span>
                <svg
                  className={`w-6 h-6 transform transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-4' : 'max-h-0'
                }`}
              >
                <div className="text-gray-600">
                  {faq.isPricing ? renderPricingAnswer() : faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
